import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import {
  FiBriefcase,
  FiTrash2,
  FiX,
  FiSearch,
  FiSettings,
  FiGlobe,
  FiEdit,
  FiCheckCircle,
  FiXCircle,
} from 'react-icons/fi';
import { C } from 'shared/darkColors';
import { empresaDominio } from 'shared/config';

const EMPTY_FORM = {
  nombre: '',
  slug: '',
  ruc: '',
  activa: true,
  telefono: '',
  email_contacto: '',
};

const fechaLocal = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
};

function EmpresasList({ onSelectEmpresa }) {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchEmpresas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await API.get('/super/empresas');
      setEmpresas(r.data.data || []);
    } catch (e) {
      setEmpresas([]);
      setError(e?.response?.data?.detail || e?.message || 'Error al cargar empresas');
      console.error('Error /super/empresas:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  const openModal = (e) => {
    setForm({
      id: e.id,
      nombre: e.nombre || '',
      slug: e.slug || '',
      ruc: e.ruc || '',
      activa: e.activa !== false,
      telefono: e.telefono || '',
      email_contacto: e.email_contacto || '',
    });
    setModal('edit');
  };

  const saveEmpresa = async () => {
    if (!form.nombre || !form.slug) return showToast('Nombre y slug requeridos', false);
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('nombre', form.nombre);
      params.append('slug', form.slug);
      params.append('ruc', form.ruc || '');
      params.append('activa', form.activa);
      params.append('telefono', form.telefono || '');
      params.append('email_contacto', form.email_contacto || '');
      await API.put(`/empresas/modificar/${form.id}?${params.toString()}`);
      showToast('Empresa actualizada');
      setModal(null);
      fetchEmpresas();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al guardar empresa';
      showToast(msg, false);
    } finally {
      setSaving(false);
    }
  };

  const deleteEmpresa = async (id) => {
    if (
      !window.confirm('Eliminar esta empresa y TODOS sus datos? Esta acción no se puede deshacer.')
    )
      return;
    try {
      await API.delete(`/empresas/eliminar/${id}`);
      showToast('Empresa eliminada');
      fetchEmpresas();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al eliminar';
      showToast(msg, false);
    }
  };

  const toggleSelection = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((e) => e.id)));
    }
  };

  const batchToggle = async (activa) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    try {
      await API.post('/super/empresas/batch/toggle', { empresa_ids: ids, activa });
      showToast(`${ids.length} empresa(s) ${activa ? 'activada(s)' : 'desactivada(s)'}`);
      setSelected(new Set());
      fetchEmpresas();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error en operacion batch';
      showToast(msg, false);
    }
  };

  const batchEliminar = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Eliminar ${ids.length} empresas? Esta acción NO se puede deshacer.`))
      return;
    try {
      const r = await API.post('/super/empresas/batch/eliminar', { empresa_ids: ids });
      const data = r.data.data;
      const msg = `${data.eliminadas.length} eliminada(s)${data.errores.length ? `, ${data.errores.length} error(es)` : ''}`;
      showToast(msg, data.errores.length === 0);
      setSelected(new Set());
      fetchEmpresas();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al eliminar';
      showToast(msg, false);
    }
  };

  const filtered = empresas.filter(
    (e) =>
      !search ||
      e.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      e.slug?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="sa-emp-wrapper" style={C.wrapper}>
      <style>{`
        @keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 768px) {
          .sa-emp-wrapper { padding: 16px 12px !important; }
          .sa-emp-section { padding: 18px !important; border-radius: 12px !important; }
          .sa-emp-section-head { flex-direction: column; gap: 12px; align-items: flex-start; }
          .sa-emp-search-wrap { width: 100% !important; }
          .sa-emp-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .sa-emp-table-wrap table { min-width: 750px; }
          .sa-emp-modal-box { max-width: 95vw !important; padding: 24px !important; max-height: 90vh !important; border-radius: 16px !important; }
          .sa-emp-form-row { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={C.topBar}>
        <div>
          <div style={C.title}>Empresas</div>
          <div style={C.subtitle}>Gestion de empresas y sus administradores</div>
        </div>
      </div>

      <div className="sa-emp-section" style={C.section}>
        <div className="sa-emp-section-head" style={C.sectionHead}>
          <div style={C.sectionTitle}>Empresas ({empresas.length})</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div className="sa-emp-search-wrap" style={C.searchWrap}>
              <FiSearch size={14} color="#9ca3af" />
              <input
                placeholder="Buscar empresa o slug..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={C.searchInput}
              />
            </div>
          </div>
        </div>
        <div className="sa-emp-table-wrap">
          {loading && (
            <div
              style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: '14px' }}
            >
              Cargando empresas...
            </div>
          )}
          {error && !loading && (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#dc2626',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}
          {!loading && !error && empresas.length === 0 && (
            <div
              style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: '14px' }}
            >
              No hay empresas registradas
            </div>
          )}
          {!loading && !error && (
            <table style={C.table}>
              <thead>
                <tr>
                  <th style={{ ...C.th, width: '40px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onChange={selectAll}
                      style={{
                        width: '15px',
                        height: '15px',
                        cursor: 'pointer',
                        accentColor: '#2563eb',
                      }}
                    />
                  </th>
                  <th style={C.th}>Nombre</th>
                  <th style={C.th}>Slug</th>
                  <th style={C.th}>Sedes</th>
                  <th style={C.th}>Plan</th>
                  <th style={C.th}>Vencimiento</th>
                  <th style={C.th}>Pago</th>
                  <th style={C.th}>Activa</th>
                  <th style={C.th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const vencida = e.estado_pago === 'vencido';
                  const alDia = e.estado_pago === 'al_dia';
                  const sinPlan = e.estado_pago !== 'al_dia' && e.estado_pago !== 'vencido';
                  return (
                    <tr key={e.id} style={{ transition: 'background 0.1s' }}>
                      <td style={{ ...C.td, width: '40px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selected.has(e.id)}
                          onChange={() => toggleSelection(e.id)}
                          style={{
                            width: '15px',
                            height: '15px',
                            cursor: 'pointer',
                            accentColor: '#2563eb',
                          }}
                        />
                      </td>
                      <td style={{ ...C.td, fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {e.nombre}
                          {e.origen_registro === 'auto' && (
                            <span
                              style={{
                                ...C.badge,
                                ...C.badgePurple,
                                padding: '2px 8px',
                                fontSize: '9px',
                              }}
                            >
                              Auto
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ ...C.td }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#2563eb',
                            fontSize: '12px',
                          }}
                        >
                          <FiGlobe size={11} /> {empresaDominio(e.slug)}
                        </span>
                      </td>
                      <td style={C.td}>{e.sede_count || 0}</td>
                      <td style={C.td}>
                        {e.plan_nombre ? (
                          <div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                flexWrap: 'wrap',
                              }}
                            >
                              <span>
                                {e.plan_nombre} ·{' '}
                                {(e.plan_tipo || 'interno').charAt(0).toUpperCase() +
                                  (e.plan_tipo || 'interno').slice(1)}{' '}
                                (${e.plan_precio})
                              </span>
                              {e.mensajes_ia_limite != null && (
                                <span
                                  style={{
                                    ...C.badge,
                                    ...C.badgePurple,
                                    padding: '2px 8px',
                                    fontSize: '10px',
                                  }}
                                >
                                  Trial
                                </span>
                              )}
                            </div>
                            {e.mensajes_ia_limite != null && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  marginTop: '4px',
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    maxWidth: '80px',
                                    height: '4px',
                                    borderRadius: '2px',
                                    background: '#f3f4f6',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${Math.min(100, ((e.mensajes_ia_usados || 0) / e.mensajes_ia_limite) * 100)}%`,
                                      height: '100%',
                                      borderRadius: '2px',
                                      background:
                                        (e.mensajes_ia_usados || 0) >= e.mensajes_ia_limite
                                          ? '#dc2626'
                                          : '#7c3aed',
                                    }}
                                  />
                                </div>
                                <span
                                  style={{
                                    fontSize: '10px',
                                    color:
                                      (e.mensajes_ia_usados || 0) >= e.mensajes_ia_limite
                                        ? '#dc2626'
                                        : '#9ca3af',
                                  }}
                                >
                                  {e.mensajes_ia_usados || 0}/{e.mensajes_ia_limite} IA
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>--</span>
                        )}
                      </td>
                      <td style={C.td}>
                        {e.fecha_fin ? (
                          fechaLocal(e.fecha_fin).toLocaleDateString()
                        ) : (
                          <span style={{ color: '#9ca3af' }}>--</span>
                        )}
                      </td>
                      <td style={C.td}>
                        <span
                          style={{
                            ...C.badge,
                            ...(alDia
                              ? C.badgeGreen
                              : vencida
                                ? C.badgeRed
                                : sinPlan
                                  ? C.badgeGray
                                  : C.badgeOrange),
                          }}
                        >
                          {alDia
                            ? 'Al dia'
                            : vencida
                              ? 'Vencido'
                              : sinPlan
                                ? 'Sin plan'
                                : e.estado_pago}
                        </span>
                      </td>
                      <td style={C.td}>
                        <span
                          style={{
                            ...C.badge,
                            ...(e.activa !== false ? C.badgeGreen : C.badgeRed),
                          }}
                        >
                          {e.activa !== false ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td style={C.td}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => openModal(e)} style={C.btnOutline}>
                            <FiEdit size={12} />
                          </button>
                          <button
                            onClick={() => onSelectEmpresa && onSelectEmpresa(e.id)}
                            style={C.btnAdmin}
                          >
                            <FiSettings size={12} /> Administrar
                          </button>
                          <button onClick={() => deleteEmpresa(e.id)} style={C.btnDanger}>
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} style={C.emptyRow}>
                      Sin resultados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && (
        <div style={C.modal} onClick={() => setModal(null)}>
          <div className="sa-emp-modal-box" style={C.modalBox} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
              }}
            >
              <div style={C.modalTitle}>Editar Empresa</div>
              <button
                onClick={() => setModal(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  padding: '4px',
                  borderRadius: '8px',
                }}
              >
                <FiX size={20} />
              </button>
            </div>

            <div
              style={{
                ...C.formLabel,
                fontSize: '13px',
                color: '#111827',
                fontWeight: 700,
                marginBottom: '18px',
                paddingBottom: '12px',
                borderBottom: '1.5px solid #f3f4f6',
              }}
            >
              <FiBriefcase size={13} style={{ marginRight: '8px' }} /> Datos de la empresa
            </div>

            <div style={C.formGroup}>
              <label style={C.formLabel}>Nombre</label>
              <input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                style={C.formInput}
              />
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Slug (subdominio)</label>
              <input
                value={form.slug}
                onChange={(e) =>
                  setForm({ ...form, slug: e.target.value.replace(/\s+/g, '-').toLowerCase() })
                }
                style={C.formInput}
                placeholder="mi-empresa"
              />
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                {form.slug ? empresaDominio(form.slug) : 'ej: mi-empresa.agorax.today'}
              </div>
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>RUC</label>
              <input
                value={form.ruc}
                onChange={(e) => setForm({ ...form, ruc: e.target.value })}
                style={C.formInput}
              />
            </div>
            <div
              className="sa-emp-form-row"
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}
            >
              <div style={C.formGroup}>
                <label style={C.formLabel}>Teléfono</label>
                <input
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  style={C.formInput}
                />
              </div>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Email de contacto</label>
                <input
                  value={form.email_contacto}
                  onChange={(e) => setForm({ ...form, email_contacto: e.target.value })}
                  style={C.formInput}
                />
              </div>
            </div>
            <div style={C.formGroup}>
              <label
                style={{
                  ...C.formLabel,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={form.activa}
                  onChange={(e) => setForm({ ...form, activa: e.target.checked })}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    accentColor: '#2563eb',
                  }}
                />{' '}
                Empresa activa
              </label>
            </div>

            <div style={C.formActions}>
              <button onClick={() => setModal(null)} style={C.btnOutline}>
                Cancelar
              </button>
              <button
                onClick={saveEmpresa}
                disabled={saving}
                style={{ ...C.btnPrimary, opacity: saving ? 0.6 : 1 }}
              >
                Actualizar
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ ...C.toast, background: toast.ok ? '#111827' : '#dc2626' }}>{toast.msg}</div>
      )}

      {selected.size > 0 && (
        <div style={C.bulkBar}>
          <span style={C.bulkCount}>
            {selected.size}{' '}
            {selected.size === 1 ? 'empresa seleccionada' : 'empresas seleccionadas'}
          </span>
          <button onClick={() => batchToggle(true)} style={C.bulkBtnGreen}>
            <FiCheckCircle size={14} /> Activar seleccionadas
          </button>
          <button onClick={() => batchToggle(false)} style={C.bulkBtnOrange}>
            <FiXCircle size={14} /> Desactivar seleccionadas
          </button>
          <button onClick={batchEliminar} style={C.bulkBtnRed}>
            <FiTrash2 size={14} /> Eliminar seleccionadas
          </button>
          <button onClick={() => setSelected(new Set())} style={C.bulkBtnGray}>
            <FiX size={14} /> Cancelar seleccion
          </button>
        </div>
      )}
    </div>
  );
}

export default EmpresasList;
